import { Button, Card, Flex, Space, Dropdown, Skeleton } from "antd"
import { useState, useCallback } from "react"
import { Paragraph, Text, Title } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { DownOutlined } from "@ant-design/icons"
import { EllipsisIcon } from "icons"
import styles from "./index.module.css"
import { colors } from "themes/variables"
import { useDispatch, useSelector } from "react-redux"
import AddEditModal from "./Modals/AddEditModal"
import { useParams } from "react-router-dom"
import DeleteModal from "./Modals/DeleteModal"
import { deleteClientNote, getClientNotes } from "store/clients"

const getActionItems = (t, onEdit, onDelete) => ({
  items: [
    {
      key: "edit",
      label: t("global.edit", "Edit")
    },
    {
      key: "delete",
      label: t("global.delete", "Delete")
    }
  ],
  onClick: ({ key, domEvent }) => {
    domEvent.stopPropagation()
    if (key === "edit") {
      onEdit()
    } else if (key === "delete") {
      onDelete()
    }
  }
})

const ClientNotes = () => {
  const { id: clientId } = useParams()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [expandedNoteId, setExpandedNoteId] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [currentNote, setCurrentNote] = useState(null)
  const [noteValue, setNoteValue] = useState("")
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [noteToDelete, setNoteToDelete] = useState(null)
  const { data, loading } = useSelector((state) => state.clients.clientNotes)
  const dispatch = useDispatch()

  const handleCancel = useCallback(() => {
    setOpen(false)
    setEditMode(false)
    setCurrentNote(null)
    setNoteValue("")
  }, [])

  const handleAddNote = useCallback(() => {
    setOpen(true)
    setEditMode(false)
    setCurrentNote(null)
    setNoteValue("")
  }, [])

  const handleEditNote = useCallback((note) => {
    setOpen(true)
    setEditMode(true)
    setCurrentNote(note)
    setNoteValue(note.content)
  }, [])

  const handleDeleteNote = useCallback((note) => {
    setNoteToDelete(note)
    setDeleteModalOpen(true)
  }, [])

  const handleDeleteCancel = useCallback(() => {
    setNoteToDelete(null)
    setDeleteModalOpen(false)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    await dispatch(deleteClientNote(noteToDelete?.id, { client_id: Number(clientId) }))
    await dispatch(getClientNotes({ client_id: Number(clientId) }))
    setDeleteModalOpen(false)
    setNoteToDelete(null)
  }, [dispatch, noteToDelete, clientId])

  // Track truncation state per note
  const [noteTruncationMap, setNoteTruncationMap] = useState({})

  const handleParagraphEllipsis = (noteId, truncated) => {
    setNoteTruncationMap((prev) => {
      if (prev[noteId] === truncated) return prev
      return { ...prev, [noteId]: truncated }
    })
  }

  return (
    <>
      <Card className={styles.notesCardContainer}>
        <Space direction="vertical" size={16} className="w-full">
          <Flex justify="space-between" align="center">
            <Title level={4}>{t("clients.notes.label")}</Title>
            <Button variant="outlined" color="primary" onClick={handleAddNote}>
              {t("clients.notes.add_note")}
            </Button>
          </Flex>
          <Skeleton loading={loading} active paragraph={{ rows: 2 }}>
            <Flex className={styles.notesListing} vertical gap={12}>
              {data?.length > 0 ? (
                data.map((note) => {
                  const isExpanded = expandedNoteId === note.id
                  const isTruncated = noteTruncationMap[note.id]
                  return (
                    <Flex vertical gap={8} key={note.id}>
                      <Flex justify="space-between" align="center">
                        <Text type="secondary">{note.date}</Text>
                        <Dropdown
                          placement="bottomRight"
                          trigger={["click"]}
                          menu={getActionItems(
                            t,
                            () => handleEditNote(note),
                            () => handleDeleteNote(note)
                          )}
                        >
                          <Button
                            type="text"
                            shape="circle"
                            className={styles.ellipsisBtn}
                            icon={<EllipsisIcon fill={colors.textSecondaryDark} width={16} height={16} className={styles.ellipsisIcon} />}
                          />
                        </Dropdown>
                      </Flex>
                      <Paragraph
                        className="mb-0"
                        ellipsis={{
                          rows: 2,
                          expanded: isExpanded,
                          onExpand: () => setExpandedNoteId(note.id),
                          onEllipsis: (truncated) => handleParagraphEllipsis(note.id, truncated)
                        }}
                      >
                        {note.content}
                      </Paragraph>
                      {isTruncated || isExpanded ? (
                        <Flex align="flex-start">
                          <Button
                            icon={<DownOutlined />}
                            type="link"
                            onClick={() => setExpandedNoteId(isExpanded ? null : note.id)}
                            className="p-0"
                          >
                            {isExpanded ? t("clients.notes.show_less") : t("clients.notes.show_more")}
                          </Button>
                        </Flex>
                      ) : null}
                    </Flex>
                  )
                })
              ) : (
                <Text>{t("clients.notes.no_notes_added")}</Text>
              )}
            </Flex>
          </Skeleton>
        </Space>
      </Card>
      {open && (
        <AddEditModal
          open={open}
          setOpen={setOpen}
          handleCancel={handleCancel}
          editMode={editMode}
          noteValue={noteValue}
          setNoteValue={setNoteValue}
          currentNote={currentNote}
          clientId={clientId}
          loading={loading}
        />
      )}
      {deleteModalOpen && (
        <DeleteModal
          loading={loading}
          noteToDelete={noteToDelete}
          deleteModalOpen={deleteModalOpen}
          setDeleteModalOpen={setDeleteModalOpen}
          handleDeleteCancel={handleDeleteCancel}
          handleDeleteConfirm={handleDeleteConfirm}
        />
      )}
    </>
  )
}
export default ClientNotes
